import { format, subDays } from 'date-fns';
import { CampaignMedium } from '@prisma/client';
import {
  CampaignFilter,
  CampaignSegment,
  PocketflowsCampaignStatus,
} from '../types/marketing';

export const STOPPABLE_CAMPAIGN_STATUSES: PocketflowsCampaignStatus[] = [
  'scheduled',
  'started',
];

export const PREBUILT_INCLUDE_SEGMENTS_IDS = [
  'ALL_CUSTOMERS',
  'ACTIVE_CUSTOMERS',
  'INACTIVE_CUSTOMERS',
  'BIGGEST_SPENDERS',
  'FREQUENT_VISITORS',
] as const;

export const PREBUILT_INCLUDE_SEGMENTS_CONTENT: Record<
  (typeof PREBUILT_INCLUDE_SEGMENTS_IDS)[number],
  {
    label: string;
    tooltip?: string;
  }
> = {
  ALL_CUSTOMERS: {
    label: 'All subscribed customers',
  },
  ACTIVE_CUSTOMERS: {
    label: 'Active customers',
    tooltip:
      'This segment includes all your customers that have either had an appointment visit within the last year, or have submitted a request within the last 6 months.',
  },
  INACTIVE_CUSTOMERS: {
    label: 'Inactive customers',
    tooltip:
      'This segment includes all your customers that have not had an appointment visit within the last year and have not submitted a request within the last 6 months.',
  },
  BIGGEST_SPENDERS: {
    label: 'Biggest spenders',
    tooltip:
      'This segment includes the top 20% of your customers with the highest spend at your business',
  },
  FREQUENT_VISITORS: {
    label: 'Frequent visitors',
    tooltip:
      'This segment includes the top 20% of your customers with the most appointment visits',
  },
};

export const getPrebuiltIncludeSegments = () => {
  const oneHundredEightyDaysAgo = format(
    subDays(new Date(), 180),
    'yyyy-MM-dd',
  );
  return {
    ALL_CUSTOMERS: {
      id: 'ALL_CUSTOMERS',
      condition: 'ALL',
      filters: [],
    },
    ACTIVE_CUSTOMERS: {
      id: 'ACTIVE_CUSTOMERS',
      condition: 'ANY',
      filters: [
        {
          category: 'LAST_APPOINTMENT_WITHIN_DAYS',
          value: 365,
          operator: 'GT',
        },
        {
          category: 'APPOINTMENT_CREATED',
          value: oneHundredEightyDaysAgo,
          operator: 'GT',
        },
      ],
    },
    INACTIVE_CUSTOMERS: {
      id: 'INACTIVE_CUSTOMERS',
      condition: 'ALL',
      filters: [
        {
          category: 'APPOINTMENT_COUNT',
          value: 0,
          operator: 'GT',
        },
        {
          category: 'LAST_APPOINTMENT_WITHIN_DAYS',
          value: 365,
          operator: 'LT',
        },
        {
          category: 'APPOINTMENT_CREATED',
          value: oneHundredEightyDaysAgo,
          operator: 'LT',
        },
      ],
    },
    BIGGEST_SPENDERS: {
      id: 'BIGGEST_SPENDERS',
      condition: 'ALL',
      filters: [
        {
          category: 'SPEND',
          value: 0,
          operator: 'GT',
        },
      ],
      topPercentage: 20,
    },
    FREQUENT_VISITORS: {
      id: 'FREQUENT_VISITORS',
      condition: 'ALL',
      filters: [
        {
          category: 'APPOINTMENT_COUNT',
          value: 1,
          operator: 'GT',
        },
      ],
      topPercentage: 20,
    },
  } satisfies Record<
    (typeof PREBUILT_INCLUDE_SEGMENTS_IDS)[number],
    CampaignSegment
  >;
};

export const PREBUILT_EXCLUDE_SEGMENTS_IDS = [
  'NO_CUSTOMERS',
  'UPCOMING_APPOINTMENT',
] as const;

export const PREBUILT_EXCLUDE_SEGMENTS_CONTENT: Record<
  (typeof PREBUILT_EXCLUDE_SEGMENTS_IDS)[number],
  {
    label: string;
    tooltip?: string;
  }
> = {
  NO_CUSTOMERS: {
    label: "Don't exclude any customers",
  },
  UPCOMING_APPOINTMENT: {
    label: 'Customers with upcoming appointments',
    tooltip:
      'This segment includes all customers with an upcoming appointment.',
  },
};

export const PREBUILT_EXCLUDE_SEGMENTS_DESCRIPTION: Record<
  (typeof PREBUILT_EXCLUDE_SEGMENTS_IDS)[number],
  string
> = {
  NO_CUSTOMERS: '',
  UPCOMING_APPOINTMENT: 'customers with an upcoming appointment',
};

export const getPrebuiltExcludeSegments = () =>
  ({
    NO_CUSTOMERS: {
      ...getPrebuiltIncludeSegments().ALL_CUSTOMERS,
      id: 'NO_CUSTOMERS',
    },
    UPCOMING_APPOINTMENT: {
      id: 'UPCOMING_APPOINTMENT',
      condition: 'ALL',
      filters: [
        {
          category: 'NEXT_APPOINTMENT_DATE',
          value: format(new Date(), 'yyyy-MM-dd'),
          operator: 'GT',
        },
      ],
    },
  }) satisfies Record<
    (typeof PREBUILT_EXCLUDE_SEGMENTS_IDS)[number],
    CampaignSegment
  >;

export const getPrebuiltSegments = () => ({
  include: getPrebuiltIncludeSegments(),
  exclude: getPrebuiltExcludeSegments(),
});

export const buildOffboardingCustomersSegment = (
  offboardingId: string,
): CampaignSegment => ({
  condition: 'ALL',
  filters: [],
  offboardingId,
});

export const DEFAULT_CAMPAIGN_FILTER: CampaignFilter = {
  category: '' as any,
  operator: 'GT',
  value: '' as any,
};

export const TEMPLATE_PLACEHOLDER_URL =
  'https://firebasestorage.googleapis.com/v0/b/bamboo-artwork-372213.appspot.com/o/public%2Fassets%2Fempty-email.svg?alt=media';

export const POCKETFLOWS_API_URL = 'https://api.pocketflows.com';

export const CREDITS_COST_CENTS: Record<CampaignMedium, number> = {
  EMAIL: 1,
  SMS: 2,
};

export const SMS_CREDITS_PER_MMS = 3;

export const MARKETING_EMBED_BORDER_COLOR = '#f2f1ed';

export enum MarketingTab {
  CAMPAIGNS = 'campaigns',
  AUTOMATIONS = 'automations',
  CUSTOM_DOMAIN = 'custom-domain',
  BRAND_KIT = 'brand-kit',
}
